import posthog from 'posthog-js'
import { useEffect } from 'react'
import { useLocation } from '@tanstack/react-router'
import { initScrollTracking, resetScrollTracking } from '@/lib/scroll-tracking'
import { initTimeOnPageTracking, resetTimeOnPageTracking } from '@/lib/time-on-page'
import { initContentEngagementTracking } from '@/lib/content-engagement'

const POSTHOG_API_KEY = import.meta.env.VITE_POSTHOG_API_KEY
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com'

export function usePostHog() {
  const location = useLocation()

  useEffect(() => {
    // Initialize PostHog if API key is available
    if (typeof POSTHOG_API_KEY === 'string' && POSTHOG_API_KEY.length > 0) {
      posthog.init(POSTHOG_API_KEY, {
        api_host: POSTHOG_HOST,
        capture_pageview: false, // We'll handle pageviews manually with router
        persistence: 'localStorage',
        disable_cookie: true,
        debug: import.meta.env.DEV,
        // Capture autocapture for common elements
        autocapture: true,
        // Performance monitoring
        capture_copypaste: true,
        capture_pageleave: true,
        // Session recording (optional, requires additional setup)
        disable_session_recording: true,
      })

      // Identify user if they're logged in
      const token = localStorage.getItem('token')
      if (token) {
        // Note: In a real app, you'd want to get user info from your auth state
        // For now, we'll just identify by a random ID
        const userId = localStorage.getItem('userId') || 'anonymous'
        posthog.identify(userId)
      }

      // Initialize enhanced tracking
      initScrollTracking({
        thresholds: [25, 50, 75, 90, 100],
        trackTimeToScroll: true,
        minTimeOnPage: 5000,
      })

      initTimeOnPageTracking({
        heartbeatInterval: 30000,
        inactiveThreshold: 60000,
        maxDuration: 1800000,
      })

      initContentEngagementTracking({
        trackReadProgress: true,
        trackContentInteractions: true,
        trackReadingSpeed: true,
        minReadTime: 60000,
      })

      console.log('[Analytics] PostHog initialized with enhanced tracking')
    } else {
      console.warn('[Analytics] PostHog API key not found. Analytics tracking disabled.')
    }
  }, [])

  useEffect(() => {
    // Track page views on route changes
    if (posthog.__loaded) {
      posthog.capture('$pageview', {
        path: location.pathname,
        search: location.searchStr,
        hash: location.hash,
      })

      // Reset tracking for new page
      resetScrollTracking()
      resetTimeOnPageTracking()

      // Track page navigation
      posthog.capture('page_navigation', {
        from: location.pathname,
        to: location.pathname,
        timestamp: Date.now(),
      })
    }
  }, [location])

  return posthog
}

// Helper functions for custom events
export const analytics = {
  // Algorithm events
  algorithmViewed: (algorithmId: string, algorithmName: string) => {
    if (posthog.__loaded) {
      posthog.capture('algorithm_viewed', {
        algorithm_id: algorithmId,
        algorithm_name: algorithmName,
      })
    }
  },

  algorithmPurchased: (
    algorithmId: string,
    algorithmName: string,
    price: number,
    pricingType: string
  ) => {
    if (posthog.__loaded) {
      posthog.capture('algorithm_purchased', {
        algorithm_id: algorithmId,
        algorithm_name: algorithmName,
        price,
        pricing_type: pricingType,
        currency: 'USD',
      })
    }
  },

  algorithmDownloaded: (algorithmId: string, algorithmName: string) => {
    if (posthog.__loaded) {
      posthog.capture('algorithm_downloaded', {
        algorithm_id: algorithmId,
        algorithm_name: algorithmName,
      })
    }
  },

  // Backtesting events
  backtestStarted: (algorithmId: string, parameters: any) => {
    if (posthog.__loaded) {
      posthog.capture('backtest_started', {
        algorithm_id: algorithmId,
        parameters,
      })
    }
  },

  backtestCompleted: (backtestId: string, duration: number) => {
    if (posthog.__loaded) {
      posthog.capture('backtest_completed', {
        backtest_id: backtestId,
        duration_seconds: duration,
      })
    }
  },

  // User events
  userRegistered: (userId: string, tradingExperience?: string) => {
    if (posthog.__loaded) {
      posthog.capture('user_registered', {
        user_id: userId,
        trading_experience: tradingExperience,
      })
    }
  },

  userLoggedIn: (userId: string) => {
    if (posthog.__loaded) {
      posthog.capture('user_logged_in', {
        user_id: userId,
      })
    }
  },

  // Developer events
  developerApplicationSubmitted: (userId: string) => {
    if (posthog.__loaded) {
      posthog.capture('developer_application_submitted', {
        user_id: userId,
      })
    }
  },

  algorithmSubmitted: (algorithmId: string, algorithmName: string) => {
    if (posthog.__loaded) {
      posthog.capture('algorithm_submitted', {
        algorithm_id: algorithmId,
        algorithm_name: algorithmName,
      })
    }
  },

  // Search events
  searchPerformed: (query: string, filters: any, resultCount: number) => {
    if (posthog.__loaded) {
      posthog.capture('search_performed', {
        query,
        filters,
        result_count: resultCount,
      })
    }
  },

  // Review events
  reviewSubmitted: (algorithmId: string, rating: number) => {
    if (posthog.__loaded) {
      posthog.capture('review_submitted', {
        algorithm_id: algorithmId,
        rating,
      })
    }
  },

  // Feature usage
  aiOptimizationUsed: (algorithmId: string) => {
    if (posthog.__loaded) {
      posthog.capture('ai_optimization_used', {
        algorithm_id: algorithmId,
      })
    }
  },

  psychologyProfileConnected: (userId: string) => {
    if (posthog.__loaded) {
      posthog.capture('psychology_profile_connected', {
        user_id: userId,
      })
    }
  },

  // Set user properties
  setUserProperties: (properties: Record<string, any>) => {
    if (posthog.__loaded) {
      posthog.setPeopleProperties(properties)
    }
  },

  // Track custom events
  track: (eventName: string, properties?: Record<string, any>) => {
    if (posthog.__loaded) {
      posthog.capture(eventName, properties)
    }
  },
}

// Graceful degradation for when PostHog is not available
export const safeAnalytics = {
  algorithmViewed: analytics.algorithmViewed,
  algorithmPurchased: analytics.algorithmPurchased,
  algorithmDownloaded: analytics.algorithmDownloaded,
  backtestStarted: analytics.backtestStarted,
  backtestCompleted: analytics.backtestCompleted,
  userRegistered: analytics.userRegistered,
  userLoggedIn: analytics.userLoggedIn,
  developerApplicationSubmitted: analytics.developerApplicationSubmitted,
  algorithmSubmitted: analytics.algorithmSubmitted,
  searchPerformed: analytics.searchPerformed,
  reviewSubmitted: analytics.reviewSubmitted,
  aiOptimizationUsed: analytics.aiOptimizationUsed,
  psychologyProfileConnected: analytics.psychologyProfileConnected,
  setUserProperties: analytics.setUserProperties,
  track: analytics.track,
}